import plur from 'plur';
import { parseDate } from '@internationalized/date';
import { bareName, fieldType, findViewCatalog } from './view-catalog';
import type { FieldType, ViewCatalog } from './view-catalog';

/**
 * The analytics query spec v1, as services/analytics/snowflake_query/spec.go
 * defines it. The server validates every field; this type only needs to be
 * wide enough to read what the agent sent and write what the form edits.
 */
export interface QuerySpec {
  version: 1;
  select?: string[];
  from: { view: string; metrics: string[]; dimensions: string[] };
  where?: Condition;
  group_by?: { columns?: string[]; time_bucket?: { field: string; unit: string } };
  order_by?: SpecOrder[];
  limit?: number;
}

export type SpecOrder = { field: string; dir: string };

export type RelativeDate = { n: number; unit: string };
export type SpecValue = string | number | boolean | Array<string | number | boolean> | { relative: RelativeDate };

export interface Condition {
  and?: Condition[];
  or?: Condition[];
  field?: string;
  op?: string;
  value?: SpecValue;
}

export type TimeWindow =
  | { mode: 'none' }
  | { mode: 'relative'; field: string; n: number; unit: string }
  /** `toOp` keeps a half-open range (`<`) half-open on the way back. */
  | { mode: 'absolute'; field: string; from: string; to: string; toOp: '<' | '<=' };

export interface FilterRow {
  field: string;
  op: string;
  value: SpecValue | undefined;
}

/**
 * The editable view of a spec. Everything the form does not understand
 * (`or` groups, nested groups, `group_by`, `select`) is carried through
 * untouched, so an edit never silently drops part of the agent's query.
 */
export interface QueryForm {
  view: string;
  metrics: string[];
  dimensions: string[];
  time: TimeWindow;
  filters: FilterRow[];
  /** Top-level conditions the form shows but cannot edit, in order. */
  opaque: Condition[];
  /** The first order_by term, the one the form edits. */
  sort: { field: string; dir: 'asc' | 'desc' } | null;
  /** The remaining order_by terms, carried through unchanged. */
  moreSort: SpecOrder[];
  limit: number | null;
  groupBy: QuerySpec['group_by'];
  select: string[] | undefined;
  /**
   * The spec as parsed. formToSpec starts from a copy of it, so top-level keys
   * this client does not know (kept by the loose parse) are written back as
   * they came.
   */
  original: QuerySpec;
}

export const RELATIVE_UNITS = ['day', 'week', 'month'] as const;

type Operator = { op: string; label: string };

// Every operator the server's spec accepts, per field type, so a spec the
// agent sent always shows the operator it has.
export const OPERATORS = {
  string: [
    { op: '=', label: 'is' },
    { op: '!=', label: 'is not' },
    { op: 'contains', label: 'contains' },
    { op: 'in', label: 'is any of' },
    { op: 'not_in', label: 'is none of' },
    { op: 'is_null', label: 'is empty' },
    { op: 'is_not_null', label: 'is set' },
  ],
  enum: [
    { op: '=', label: 'is' },
    { op: '!=', label: 'is not' },
    { op: 'in', label: 'is any of' },
    { op: 'not_in', label: 'is none of' },
  ],
  number: [
    { op: '>=', label: 'at least' },
    { op: '<=', label: 'at most' },
    { op: '=', label: 'equals' },
    { op: '!=', label: 'is not' },
    { op: '>', label: 'more than' },
    { op: '<', label: 'less than' },
    { op: 'in', label: 'is any of' },
    { op: 'not_in', label: 'is none of' },
  ],
  bool: [{ op: '=', label: 'is' }],
  date: [
    { op: '>=', label: 'on or after' },
    { op: '<=', label: 'on or before' },
    { op: '>', label: 'after' },
    { op: '<', label: 'before' },
    { op: '=', label: 'on' },
    { op: '!=', label: 'not on' },
  ],
} satisfies Record<FieldType, Operator[]>;

const VALUELESS_OPS = new Set(['is_null', 'is_not_null']);
const LIST_OPS = new Set(['in', 'not_in']);

export function opTakesValue(op: string): boolean {
  return !VALUELESS_OPS.has(op);
}

/** `in` and `not_in` take a list; every other operator a single value. */
export function opTakesList(op: string): boolean {
  return LIST_OPS.has(op);
}

/** The operators to offer for a row: the type's list, plus the row's own if it is not there. */
export function operatorsFor(type: FieldType, current: string): Operator[] {
  const ops: Operator[] = OPERATORS[type];
  return ops.some((o) => o.op === current) ? ops : [...ops, { op: current, label: current }];
}

function isLeaf(c: Condition): boolean {
  return c.field != null && c.and == null && c.or == null;
}

/** The rolling window a value carries, or null for any other value. */
function relativeOf(v: SpecValue | undefined): RelativeDate | null {
  if (typeof v !== 'object' || v == null || Array.isArray(v) || !('relative' in v)) return null;
  return v.relative;
}

export function isDateField(catalog: ViewCatalog | null, field: string): boolean {
  if (catalog == null) return bareName(field).endsWith('_date');
  return catalog.dateFields.includes(field) || fieldType(catalog, field) === 'date';
}

type SplitTime = { time: TimeWindow; rest: Condition[] };

/**
 * Pulls the time window out of the top-level conditions: a `>=` on a date
 * field with a relative value is a rolling window; `>=` and `<=` string dates
 * on the same field are an exact range. Everything else is a filter.
 */
function splitTime(catalog: ViewCatalog | null, leaves: Condition[]): SplitTime {
  const start = leaves.find((c) => c.op === '>=' && isDateField(catalog, c.field ?? ''));
  if (start == null || start.field == null) return { time: { mode: 'none' }, rest: leaves };

  const relative = relativeOf(start.value);
  if (relative != null) {
    const rest = leaves.filter((c) => c !== start);
    return { time: { mode: 'relative', field: start.field, n: relative.n, unit: relative.unit }, rest };
  }

  if (typeof start.value === 'string') {
    // Only a dated upper bound closes the range; any other bound stays a filter row.
    const end = leaves.find(
      (c) => c !== start && c.field === start.field && (c.op === '<=' || c.op === '<') && typeof c.value === 'string',
    );
    const to = typeof end?.value === 'string' ? end.value : '';
    const toOp = end?.op === '<' ? '<' : '<=';
    const rest = leaves.filter((c) => c !== start && c !== end);
    return { time: { mode: 'absolute', field: start.field, from: start.value, to, toOp }, rest };
  }

  return { time: { mode: 'none' }, rest: leaves };
}

/**
 * The last day an exact range includes, as an ISO date: the bound itself for
 * `<=`, the day before for `<`. Empty when the range has no end. This is what
 * an inclusive date picker should show for the range.
 */
export function lastIncludedDay(time: Extract<TimeWindow, { mode: 'absolute' }>): string {
  if (time.to === '' || time.toOp === '<=') return time.to;
  try {
    return parseDate(time.to).subtract({ days: 1 }).toString();
  } catch {
    return time.to;
  }
}

export function specToForm(spec: QuerySpec): QueryForm {
  const catalog = findViewCatalog(spec.from.view);
  const where = spec.where;
  const top: Condition[] = where == null ? [] : (where.and ?? [where]);
  const leaves = top.filter(isLeaf);
  const opaque = top.filter((c) => !isLeaf(c));
  const { time, rest } = splitTime(catalog, leaves);

  const [first, ...moreSort] = spec.order_by ?? [];

  return {
    view: spec.from.view,
    metrics: [...spec.from.metrics],
    dimensions: [...spec.from.dimensions],
    time,
    filters: rest.map((c) => ({ field: c.field ?? '', op: c.op ?? '=', value: c.value })),
    opaque,
    sort: first == null ? null : { field: first.field, dir: first.dir.toLowerCase() === 'asc' ? 'asc' : 'desc' },
    moreSort,
    limit: spec.limit ?? null,
    groupBy: spec.group_by,
    select: spec.select,
    original: spec,
  };
}

/** The top-level conditions that write a time window. */
export function timeConditions(time: TimeWindow): Condition[] {
  switch (time.mode) {
    case 'none':
      return [];
    case 'relative':
      return [{ field: time.field, op: '>=', value: { relative: { n: Math.max(1, time.n), unit: time.unit } } }];
    case 'absolute': {
      const out: Condition[] = [];
      if (time.from !== '') out.push({ field: time.field, op: '>=', value: time.from });
      if (time.to !== '') out.push({ field: time.field, op: time.toOp, value: time.to });
      return out;
    }
  }
}

function filterCondition(row: FilterRow): Condition | null {
  if (row.field === '') return null;
  if (!opTakesValue(row.op)) return { field: row.field, op: row.op };
  if (row.value == null || row.value === '') return null;
  if (Array.isArray(row.value) && row.value.length === 0) return null;
  return { field: row.field, op: row.op, value: row.value };
}

type AddedNames = { dimensions: string[]; metrics: string[] };

/**
 * The bare names of the members the customer added that the agent's spec did
 * not read. A spec with `select` or `group_by` returns only what those name, so
 * an added member is written into them too, or it never reaches the output.
 */
function addedNames(form: QueryForm): AddedNames {
  const before = new Set([...form.original.from.dimensions, ...form.original.from.metrics]);
  const fresh = (members: string[]) => members.filter((m) => !before.has(m)).map(bareName);
  return { dimensions: fresh(form.dimensions), metrics: fresh(form.metrics) };
}

/** `names`, then each of `extra` it does not already have. */
function appendNew(names: string[], extra: string[]): string[] {
  return [...names, ...extra.filter((name) => !names.includes(name))];
}

/**
 * The output names a spec returns, in order: what `order_by` may name. Mirrors
 * the compiler's default select, or `select` when the spec sets one.
 */
export function outputNames(form: QueryForm): string[] {
  const added = addedNames(form);
  if (form.select != null && form.select.length > 0) {
    return appendNew(form.select, [...added.dimensions, ...added.metrics]);
  }
  const metrics = form.metrics.map(bareName);
  if (form.groupBy != null) {
    const kept = appendNew(form.groupBy.columns ?? [], added.dimensions);
    const bucket = form.groupBy.time_bucket?.unit;
    return [...kept, ...(bucket != null ? [bucket] : []), ...metrics];
  }
  return [...form.dimensions.map(bareName), ...metrics];
}

export function formToSpec(form: QueryForm): QuerySpec {
  const conditions = timeConditions(form.time);
  for (const row of form.filters) {
    const condition = filterCondition(row);
    if (condition != null) conditions.push(condition);
  }
  conditions.push(...form.opaque);

  const dims = new Set(form.dimensions.map(bareName));
  const metrics = new Set(form.metrics.map(bareName));
  const added = addedNames(form);

  // Every key the form owns is rebuilt below; what remains of the copy is what
  // the form does not know and must not drop.
  const spec: QuerySpec = {
    ...form.original,
    version: 1,
    from: { view: form.view, metrics: form.metrics, dimensions: form.dimensions },
  };
  delete spec.select;
  delete spec.where;
  delete spec.group_by;
  delete spec.order_by;
  delete spec.limit;

  if (conditions.length === 1) spec.where = conditions[0];
  else if (conditions.length > 1) spec.where = { and: conditions };

  // A dimension the customer removed leaves group_by and select too, so the
  // server never sees a name the spec no longer reads; one they added joins
  // them, so it is grouped by and returned instead of aggregated away.
  let bucket: string | null = null;
  if (form.groupBy != null) {
    const columns = appendNew(
      (form.groupBy.columns ?? []).filter((c) => dims.has(c)),
      added.dimensions,
    );
    const timeBucket =
      form.groupBy.time_bucket != null && dims.has(form.groupBy.time_bucket.field)
        ? form.groupBy.time_bucket
        : undefined;
    const groupBy: QuerySpec['group_by'] = {};
    if (columns.length > 0) groupBy.columns = columns;
    if (timeBucket != null) {
      groupBy.time_bucket = timeBucket;
      bucket = timeBucket.unit;
    }
    if (columns.length > 0 || timeBucket != null) spec.group_by = groupBy;
  }

  const names = new Set([...dims, ...metrics, ...(bucket != null ? [bucket] : [])]);
  if (form.select != null) {
    const select = appendNew(
      form.select.filter((name) => names.has(name)),
      [...added.dimensions, ...added.metrics],
    );
    if (select.length > 0) spec.select = select;
  }

  // The form edits the first order_by term; the rest ride along, minus any
  // whose column is gone or that the first term now names (the server rejects
  // a column ordered twice).
  const orderBy: SpecOrder[] = [];
  if (form.sort != null && names.has(form.sort.field)) orderBy.push({ field: form.sort.field, dir: form.sort.dir });
  orderBy.push(...form.moreSort.filter((o) => names.has(o.field) && o.field !== form.sort?.field));
  if (orderBy.length > 0) spec.order_by = orderBy;
  if (form.limit != null && form.limit > 0) spec.limit = form.limit;

  return spec;
}

/** One line per condition the form cannot edit, for the read-only row. */
export function describeCondition(c: Condition): string {
  if (c.and != null) return `(${c.and.map(describeCondition).join(' and ')})`;
  if (c.or != null) return `(${c.or.map(describeCondition).join(' or ')})`;
  const relative = relativeOf(c.value);
  const value =
    relative != null
      ? `last ${relative.n} ${plur(relative.unit, relative.n)}`
      : Array.isArray(c.value)
        ? c.value.join(', ')
        : c.value == null
          ? ''
          : String(c.value);
  return `${bareName(c.field ?? '')} ${c.op ?? ''} ${value}`.trim();
}

/** `unique_human_open_rate` reads as "unique human open rate". */
function humanName(member: string): string {
  return bareName(member).replaceAll('_', ' ');
}

function describeTime(time: TimeWindow): string {
  switch (time.mode) {
    case 'none':
      return 'with no time window';
    case 'relative':
      return `for the last ${time.n} ${plur(time.unit, time.n)}`;
    case 'absolute': {
      // A range always has a start: a lone end bound round-trips as a filter row.
      const to = lastIncludedDay(time);
      return `from ${time.from} to ${to === '' ? 'now' : to}`;
    }
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A filter as the customer would say it: "only channel email", "with at least 100 sent". */
function filterPhrase(c: Condition): string {
  // A group nested in a group of the other kind is bracketed.
  if (c.and != null) return c.and.map((x) => (x.or != null ? `(${filterPhrase(x)})` : filterPhrase(x))).join(' and ');
  if (c.or != null) return c.or.map((x) => (x.and != null ? `(${filterPhrase(x)})` : filterPhrase(x))).join(' or ');
  const name = humanName(c.field ?? '');
  const relative = relativeOf(c.value);
  const isDate = relative != null || (typeof c.value === 'string' && ISO_DATE.test(c.value));
  const value =
    relative != null
      ? `the last ${relative.n} ${plur(relative.unit, relative.n)}`
      : Array.isArray(c.value)
        ? c.value.map(String).join(' or ')
        : String(c.value ?? '');
  switch (c.op) {
    case '=':
      return typeof c.value === 'string' && !isDate ? `only ${name} ${value}` : `${name} is ${value}`;
    case '!=':
      return `${name} is not ${value}`;
    case 'in':
      return `${name} is ${value}`;
    case 'not_in':
      return `${name} is not ${value}`;
    case '>=':
      if (relative != null) return `${name} in ${value}`;
      return isDate ? `${name} on or after ${value}` : `with at least ${value} ${name}`;
    case '<=':
      return isDate ? `${name} on or before ${value}` : `with at most ${value} ${name}`;
    case '>':
      return isDate ? `${name} after ${value}` : `with more than ${value} ${name}`;
    case '<':
      return isDate ? `${name} before ${value}` : `with less than ${value} ${name}`;
    case 'contains':
      return `${name} contains ${value}`;
    case 'is_null':
      return `${name} is empty`;
    case 'is_not_null':
      return `${name} is set`;
    default:
      return `${name} ${c.op ?? ''} ${value}`.trim();
  }
}

/**
 * A filter the form shows, keyed by what it filters on: the field's human name
 * for an editable row, the whole phrase for an opaque group (which has no one
 * field, so each group is its own key).
 */
type FilterEntry = { key: string; label: string; phrase: string };

function filterEntries(form: QueryForm): FilterEntry[] {
  const entries: FilterEntry[] = [];
  for (const row of form.filters) {
    const condition = filterCondition(row);
    if (condition == null) continue;
    const name = humanName(row.field);
    entries.push({ key: name, label: `the ${name} filter`, phrase: filterPhrase(condition) });
  }
  for (const c of form.opaque) {
    const phrase = filterPhrase(c);
    entries.push({ key: `opaque:${phrase}`, label: `the "${phrase}" filter`, phrase });
  }
  return entries;
}

type ListDiff = { added: string[]; removed: string[] };

/** Items in `after` that `before` lacks, and the reverse, counting repeats. */
function diffLists(before: string[], after: string[]): ListDiff {
  const count = (items: string[]) => {
    const counts = new Map<string, number>();
    for (const item of items) counts.set(item, (counts.get(item) ?? 0) + 1);
    return counts;
  };
  const was = count(before);
  const now = count(after);
  const added: string[] = [];
  const removed: string[] = [];
  for (const [item, n] of now) for (let i = n - (was.get(item) ?? 0); i > 0; i--) added.push(item);
  for (const [item, n] of was) for (let i = n - (now.get(item) ?? 0); i > 0; i--) removed.push(item);
  return { added, removed };
}

function list(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function sortPhrase(form: QueryForm): string | null {
  if (form.sort == null) return form.limit == null ? null : `the first ${form.limit} rows`;
  const name = humanName(form.sort.field);
  if (form.sort.dir === 'desc' && form.limit != null) return `top ${form.limit} by ${name}`;
  const order = `sorted by ${name}, ${form.sort.dir === 'asc' ? 'lowest' : 'highest'} first`;
  return form.limit == null ? order : `${order}, ${form.limit} rows`;
}

/**
 * The edits between two forms as clauses of one sentence, in the order people
 * say them: time window, what is measured, grouping, filters, then sort and
 * limit. "for the last 10 weeks", "also showing delivered", "top 25 by unique
 * human open rate". Empty when nothing changed.
 *
 * Both forms are first taken through the spec and back, so the clauses
 * describe what the spec actually carries: a sort whose column was removed
 * reads as unsorted, a window of 0 as the 1 the spec sends.
 */
export function describeEdits(rawBefore: QueryForm, rawAfter: QueryForm): string[] {
  const before = specToForm(formToSpec(rawBefore));
  const after = specToForm(formToSpec(rawAfter));
  const clauses: string[] = [];

  if (JSON.stringify(before.time) !== JSON.stringify(after.time)) clauses.push(describeTime(after.time));

  const metrics = diffLists(before.metrics.map(humanName), after.metrics.map(humanName));
  if (metrics.added.length > 0) clauses.push(`also showing ${list(metrics.added)}`);
  if (metrics.removed.length > 0) clauses.push(`without ${list(metrics.removed)}`);

  const dims = diffLists(before.dimensions.map(humanName), after.dimensions.map(humanName));
  if (dims.added.length > 0) clauses.push(`broken down by ${list(dims.added)}`);
  if (dims.removed.length > 0) clauses.push(`no longer by ${list(dims.removed)}`);

  // A removed filter phrase is a value change when the same field also gained a
  // phrase (the new value alone says it); otherwise that filter was dropped.
  const filtersBefore = filterEntries(before);
  const filtersAfter = filterEntries(after);
  const filters = diffLists(
    filtersBefore.map((f) => f.phrase),
    filtersAfter.map((f) => f.phrase),
  );
  clauses.push(...filters.added);
  // Each added phrase accounts for one removal on its key.
  const addedPhrases = [...filters.added];
  const addedPerKey = new Map<string, number>();
  for (const f of filtersAfter) {
    const at = addedPhrases.indexOf(f.phrase);
    if (at === -1) continue;
    addedPhrases.splice(at, 1);
    addedPerKey.set(f.key, (addedPerKey.get(f.key) ?? 0) + 1);
  }
  const removed = [...filters.removed];
  const dropped = new Set<string>();
  for (const f of filtersBefore) {
    const at = removed.indexOf(f.phrase);
    if (at === -1) continue;
    removed.splice(at, 1);
    const left = addedPerKey.get(f.key) ?? 0;
    if (left > 0) addedPerKey.set(f.key, left - 1);
    else dropped.add(f.label);
  }
  if (dropped.size > 0) clauses.push(`without ${list([...dropped])}`);

  const sortBefore = sortPhrase(before);
  const sortAfter = sortPhrase(after);
  if (sortBefore !== sortAfter) {
    if (sortAfter == null) {
      clauses.push(before.sort == null ? 'all rows' : 'unsorted, all rows');
    } else if (before.limit != null && after.limit == null) {
      // The sort phrase alone would hide that the cap went away.
      clauses.push(`${sortAfter}, all rows`);
    } else {
      clauses.push(sortAfter);
    }
  }
  const primaryAfter = after.sort == null ? '' : humanName(after.sort.field);
  const more = diffLists(
    before.moreSort.map((o) => humanName(o.field)),
    after.moreSort.map((o) => humanName(o.field)),
  );
  if (more.added.length > 0) clauses.push(`then by ${list(more.added)}`);
  const noLonger = more.removed.filter((name) => name !== primaryAfter);
  if (noLonger.length > 0) clauses.push(`no longer sorted by ${list(noLonger)}`);

  return clauses;
}

/**
 * The user turn that asks the agent to run an edited query: the earlier
 * question, the edits as one sentence, then the exact spec so the agent sends
 * it as is (query_spec.md tells it to run a pasted spec unchanged and to keep
 * the quoted question for the link). The question is quoted whole: the agent
 * carries it into the next link, so any shortening here would compound.
 * `workspaceId` names the workspace the query was previewed in; pass it only
 * when that is not the page's own, so the agent runs it there rather than in
 * the conversation's.
 */
export function rerunMessage(question: string, edits: string[], spec: QuerySpec, workspaceId?: number): string {
  const quoted = question.trim().replaceAll('"', "'");
  const where = workspaceId == null ? '' : `, in workspace ${workspaceId}`;
  const summary =
    edits.length === 0
      ? `Same question, "${quoted}", run again${where}.`
      : `Same question, "${quoted}", but ${edits.join(', ')}${where}.`;
  return `${summary}\n\n\`\`\`json\n${JSON.stringify(spec, null, 2)}\n\`\`\``;
}

export type ChangedLines = {
  /** Indexes of lines in `next` that `previous` does not have, by exact text. */
  addedAt: Set<number>;
  added: number;
  /** Lines of `previous` that `next` no longer has: an edit that only removes a clause. */
  removed: number;
};

export function changedLines(previous: string, next: string): ChangedLines {
  const before = previous.split('\n');
  const after = next.split('\n');
  const seenBefore = new Set(before);
  const seenAfter = new Set(after);
  const addedAt = new Set<number>();
  after.forEach((line, i) => {
    if (!seenBefore.has(line)) addedAt.add(i);
  });
  const removed = before.filter((line) => !seenAfter.has(line)).length;
  return { addedAt, added: addedAt.size, removed };
}

export function defaultValueFor(type: FieldType, options: string[] | undefined): SpecValue {
  switch (type) {
    case 'number':
      return 0;
    case 'bool':
      return true;
    case 'enum':
      return options?.[0] ?? '';
    default:
      return '';
  }
}
